<script setup>
import Select from "@/Components/Select.vue";
import BreezeButton from "@/Components/AuthButton.vue";
import BreezeGuestLayout from "@/Layouts/Guest.vue";
import BreezeInput from "@/Components/Input.vue";
import BreezeLabel from "@/Components/Label.vue";
import BreezeValidationErrors from "@/Components/ValidationErrors.vue";
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";

let props = defineProps({
  invitation: Object,
  role: String,
});

const form = useForm({
  firstname: props.invitation.firstname,
  lastname: props.invitation.lastname,
  email: props.invitation.email,
  password: "",
  password_confirmation: "",
  username: "",
  salutation: props.invitation.salutation,
  title: "",
  tel: "",
  job: "",
  role: props.invitation.email,
  token: props.invitation.token,
});

const submit = () => {
  form.post("/registerInvitation", {
    onFinish: () => form.reset("password", "password_confirmation"),
  });
};
</script>

<template>
  <BreezeGuestLayout>
    <div
      class="w-full sm:max-w-4xl mt-6 px-24 py-12 bg-oteoBlue-600 shadow-md overflow-hidden sm:rounded-lg"
    >
      <Head title="Register" />

      <BreezeValidationErrors class="mb-4 p-6 rounded bg-oteo-200" />

      <h2 class="text-3xl text-white mb-4">Registrierung</h2>

      <form @submit.prevent="submit">
        <div class="mt-6 mb-6 text-white font-bold flex items-center">
          Anrede
          <input
            class="ml-4"
            v-model="form.salutation"
            id="s_female"
            type="radio"
            value="f"
          />
          <BreezeLabel
            class="inline ml-2 font-normal"
            for="s_female"
            value="Frau"
          />

          <input
            class="ml-4"
            id="s_male"
            type="radio"
            v-model="form.salutation"
            value="m"
          />
          <BreezeLabel
            class="inline ml-2 font-normal"
            for="s_male"
            value="Herr"
          />

          <input
            class="ml-4"
            id="s_none"
            type="radio"
            v-model="form.salutation"
            value="n"
          />
          <BreezeLabel
            class="inline ml-2 font-normal"
            for="s_none"
            value="Keine Angabe"
          />
        </div>

        <div class="grid grid-cols-2 gap-5">
          <div class="">
            <BreezeLabel for="firstname" value="Vorname" />
            <BreezeInput
              id="firstname"
              type="text"
              class="mt-1 block w-full"
              v-model="form.firstname"
              required
              autofocus
              placeholder="Bitte eingeben"
              autocomplete="given-name"
            />
          </div>

          <div class="">
            <BreezeLabel for="lastname" value="Nachname" />
            <BreezeInput
              id="lastname"
              type="text"
              class="mt-1 block w-full"
              v-model="form.lastname"
              required
              placeholder="Bitte eingeben"
              autocomplete="family-name"
            />
          </div>

          <div class="">
            <BreezeLabel for="email" value="E-Mail" />
            <BreezeInput
              id="email"
              type="email"
              class="mt-1 block w-full"
              v-model="form.email"
              required
              autocomplete="email"
              placeholder="Bitte eingeben"
              disabled
            />
          </div>

          <div class="">
            <BreezeLabel for="tel" value="Telefonnummer (Notfall-Kontakt)" />
            <BreezeInput
              id="tel"
              type="tel"
              class="mt-1 block w-full"
              v-model="form.tel"
              required
              autocomplete="tel"
              placeholder="Bitte eingeben"
            />
          </div>

          <div class="">
            <BreezeLabel
              for="username"
              value="Kürzel(max 3 Zeichen; gilt als Benutzername)"
            />
            <BreezeInput
              id="username"
              type="text"
              class="mt-1 block w-full"
              v-model="form.username"
              required
              maxlength="3"
              placeholder="Bitte eingeben"
              autocomplete="username"
            />
          </div>

          <div class="">
            <BreezeLabel for="title" value="Titel" />
            <Select
              v-model="form.title"
              :isAuth="true"
              class="mt-1 block w-full"
            >
              <option disabled value="" class="">Bitte wählen</option>
              <option value=""></option>
              <option value="Dr.">Dr.</option>
              <option value="Dr. med.">Dr. med.</option>
              <option value="Prof. Dr. med.">Prof. Dr. med.</option>
            </Select>
          </div>

          <div class="">
            <BreezeLabel for="job" value="Tätigkeit" />
            <Select v-model="form.job" :isAuth="true" class="mt-1 block w-full">
              <option disabled value="" class="">Bitte wählen</option>
              <option value="Zahnarzt/in">Zahnarzt/in</option>
              <option value="Zahnmedizinische Prophylaxe">
                Zahnmedizinische Prophylaxe
              </option>
              <option value="Zahnmedizinische Fachangestellte/r">
                Zahnmedizinische Fachangestellte/r
              </option>
            </Select>
          </div>

          <div class="">
            <BreezeLabel for="role" value="Rolle" />
            <Select v-model="role" :isAuth="true" class="mt-1 block w-full">
              <option disabled :value="role" class="">{{ role }}</option>
            </Select>
          </div>

          <div class="">
            <BreezeLabel for="password" value="Passwort" />
            <BreezeInput
              id="password"
              type="password"
              class="mt-1 block w-full"
              v-model="form.password"
              required
              placeholder="Bitte eingeben"
              autocomplete="password"
            />
          </div>

          <div class="">
            <BreezeLabel
              for="password_confirmation"
              value="Passwort wiederholen"
            />
            <BreezeInput
              id="password_confirmation"
              type="password"
              class="mt-1 block w-full"
              v-model="form.password_confirmation"
              required
              placeholder="Bitte eingeben"
              autocomplete="password_confirmation"
            />
          </div>
        </div>

        <div class="flex items-center justify-between mt-4">
          <Link
            :href="route('login')"
            class="underline text-sm text-white hover:text-gray-300"
          >
            Schon registriert?
          </Link>

          <BreezeButton
            class="ml-4 w-56 justify-center"
            :class="{ 'opacity-25': form.processing }"
            :disabled="form.processing"
          >
            Registrieren
          </BreezeButton>
        </div>
      </form>
    </div>
  </BreezeGuestLayout>
</template>
